import { Injectable } from '@angular/core';
import { WorkerActivity } from '@shared/models/worker-activity.model';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  // --- State ---
  private worker$ = new BehaviorSubject<string>(localStorage.getItem('worker'));
  private productionOrder$ = new BehaviorSubject<string>(localStorage.getItem('productionOrder') || '');

  constructor() { }

  // --- Generic Storage Methods ---

  /** Retrieves a string value from local storage. */
  public get(key: string): Promise<string> {
    const cache = localStorage.getItem(key);
    return cache ? Promise.resolve(cache) : Promise.reject();
  }

  /** Sets a string value in local storage. */
  public set(key: string, value: string): Promise<boolean> {
    localStorage.setItem(key, value);
    return Promise.resolve(true);
  }

  /** Removes a specific item from local storage. */
  public remove(key: string): Promise<boolean> {
    localStorage.removeItem(key);
    return Promise.resolve(true);
  }

  /** Clears all local storage. */
  public clear(): Promise<boolean> {
    localStorage.clear();
    return Promise.resolve(true);
  }

  // --- Business Logic Methods ---

  /** Constructs a WorkerActivity object from various stored items. */
  public getWorkerActivity(): Promise<WorkerActivity> {
    const phase = JSON.parse(localStorage.getItem('phase'));
    const worker = JSON.parse(localStorage.getItem('worker'));
    const productionOrder = JSON.parse(localStorage.getItem('productionOrder'));
    
    if (!worker || !phase || !productionOrder) {
      return Promise.reject();
    }

    const job = localStorage.getItem('job');
    const process = localStorage.getItem('process');
    const degreasing_passes = localStorage.getItem('degreasing_passes');
    const finishing_passes = localStorage.getItem('finishing_passes');
    const data = JSON.parse(localStorage.getItem('data'));
    const startTime = localStorage.getItem('start_time');
    const endTime = localStorage.getItem('end_time');
    const currentWa = JSON.parse(localStorage.getItem('workerActivity') || '{}');

    let wa = new WorkerActivity();
    wa.worker_id = worker.id;
    wa.item_id = productionOrder.item_id;
    wa.item_lot = productionOrder.item_lot;
    wa.production_order_id = productionOrder.id;
    wa.production_order_code = productionOrder.code;
    wa.mbom_row_id = productionOrder.mbom_row_id;
    wa.quantity_done = (data?.piecies) ? data.piecies : null;
    wa.phase_id = phase.id;
    wa.phase_name = phase.name;
    wa.start_date = (startTime) ? new Date(startTime) : null;
    wa.data = (data) ? data : null;
    wa.id = (currentWa?.id) ? currentWa?.id : 0;

    if (endTime && endTime != 'NULL') {
      wa.end_date = new Date(endTime);
      wa.endJob = 1;
    }

    wa.job = 'NULL';
    wa.process = 'NULL';
    wa.passes = 'NULL';

    // Specific logic for Rettifica (RE) or Tempra (TE)
    if ((phase.code == 'RE' || phase.code == 'TE') && job && process) {
      wa.job = job;
      wa.process = process;
      if (degreasing_passes && degreasing_passes !== 'NULL') wa.passes = degreasing_passes;
      if (finishing_passes && finishing_passes !== 'NULL') wa.passes = finishing_passes;
    }

    return Promise.resolve(wa);
  }

  /** Clears specific session data related to the current workflow. */
  public clearMainData() {
    const keys = [
      'productionOrder', 'data', 'phase', 'workerActivity', 
      'new_wa_id', 'start_time', 'end_time', 'end_job'
    ];
    keys.forEach(key => localStorage.removeItem(key));
  }

  // --- Observables Getters/Setters ---

  public getWorker(): Observable<string> {
    return this.worker$.asObservable();
  }

  public setWorker(value: any) {
    this.worker$.next(value);
  }

  public getProductionOrder(): Observable<string> {
    return this.productionOrder$.asObservable();
  }

  public setProductionOrder(value: any) {
    this.productionOrder$.next(value);
  }
}
